import type { GridApi } from 'ag-grid-community';
import type { Entity, EntityType, Reference } from '@platform/entities/entity.class';
import { GridStore } from '@platform/store';
import { v4 as guid } from 'uuid';
import { do_rpc_query } from '@platform/components/rpc';
import type { RpcFilters } from '@platform/shared/rpc/node/rpc-helpers';

type DeferredCall = {
    onSuccess: () => void;
    onError: (error?: Error) => void;
} & (
    | {
          entity: typeof Entity;
          data: Array<Entity>;
          applyFn: (id: string) => Record<string, any>;
      }
    | { saveFn: (props: any) => Promise<void>; props: any }
);

type DepParams<RK, T> =
    | { idField: string }
    | { preprocess: (parent: EntityType<RK>, child: EntityType<T>) => EntityType<T> };

export class Dao<T extends Entity, R extends typeof Entity> {
    private deferred: DeferredCall[] = [];
    private hooks = { beforeSave: [] as Array<(data: EntityType<T>[]) => void> };
    protected unsavedData: Map<string, EntityType<T>> = new Map();
    protected daoChildren: { dao: Dao<any, any>; params: DepParams<any, any> }[] = [];
    protected isChildren = false;
    protected gridId: string | null = null;

    constructor(public entityClass: R) {
        this.entityClass = entityClass;
    }

    protected load(
        params: {
            page?: number | [page: number, pageSize?: number];
            sqlFilter?: RpcFilters | string | null;
            fields?: string[];
            id?: string | null;
            readType?: null | 'template' | 'any';
        } & Record<string, any>
    ): Promise<EntityType<T>[]> {
        const dataStructure = this.entityClass.getDataStructure();
        if (params.fields && Array.isArray(params.fields)) {
            dataStructure.structure = dataStructure.structure.filter(({ field }) => params.fields?.includes(field));
        }
        return do_rpc_query(
            'read' + (params.readType ? `_${params.readType}` : ''),
            this.entityClass._tableInfo?.tableName,
            {
                sqlFilter: params.sqlFilter,
                page: params.page,
            },
            () => dataStructure
        );
    }
    protected preprocess(data: Array<EntityType<T>>): Promise<EntityType<T>[]> | EntityType<T>[] {
        return data;
    }
    protected preSave(data: Array<EntityType<T>>): Promise<EntityType<T>[]> | EntityType<T>[] {
        return data;
    }
    protected async save(data: Array<EntityType<T>>, typeSave = 'modify'): Promise<void> {
        if (!data.length) return;

        return do_rpc_query(
            typeSave,
            this.entityClass._tableInfo?.tableName,
            data,
            () => this.entityClass.getDataStructure(),
            this.getGridApi()?.()
        );
    }

    public create(data: Partial<EntityType<T>> | undefined): EntityType<T> | Promise<EntityType<T>> {
        // @ts-ignore
        return {
            id: guid().toLocaleLowerCase(),
            sys_row_status: 'NEW',
            ...data,
        };
    }

    public loadAll(params: { sqlFilter?: RpcFilters | string | null; fields?: string[] } & Record<string, any>) {
        return this.load(params).then(this.preprocess.bind(this));
    }

    public loadOne(
        params: {
            id: Reference;
            sqlFilter?: RpcFilters | string;
            fields?: string[];
            byEmployee?: boolean;
            readType?: null | 'template' | 'any';
            noFilterByOrg?: boolean;
        } & Record<string, any>
    ): Promise<EntityType<T>> {
        const id = typeof params.id === 'string' ? params.id : params.id?.value;
        if (!id) return Promise.reject(new Error('id is null'));
        const sqlFilter = `_main_table_.id = '${id}'` + (params.sqlFilter ? ` AND (${params.sqlFilter})` : '');
        return this.load({
            sqlFilter,
            fields: params.fields,
            id,
            readType: params.readType,
            noFilterByOrg: params.noFilterByOrg,
        })
            .then(this.preprocess.bind(this))
            .then((data) => data?.[0] || null);
    }

    public async checkOrLoadOrCreate(
        item: EntityType<T> | string | null | undefined
    ): Promise<{ item: EntityType<T>; isNew: boolean }> {
        if (typeof item === 'object' && item?.id) return { item, isNew: false };
        if (typeof item === 'string')
            return { item: await this.loadOne({ id: item, byEmployee: false }), isNew: false };
        else return { item: await this.create(item || {}), isNew: true };
    }

    public async checkOrLoadAll(
        items: EntityType<T>[] | null | undefined,
        option: any
    ): Promise<{ items: EntityType<T>[] | null }> {
        if (Array.isArray(items) && items?.length) return { items };
        if (!option) return { items: null };
        return { items: await this.loadAll(option) };
    }

    public async saveAll(
        data: Array<EntityType<T>> | EntityType<T>,
        forced = false,
        typeSave: 'modify' | 'save_template' | 'savedraft' | 'delete' = 'modify'
    ): Promise<any> {
        const dataToSave = Array.isArray(data) ? data : [data];
        if (dataToSave.length === 0) console.debug('No data to save!');
        this.hooks.beforeSave.forEach((fn) => fn(dataToSave));
        const actualDataToSave = await this.preSave(dataToSave);
        if (this.isChildren && !forced) {
            this.unsavedData = new Map([
                ...this.unsavedData.entries(),
                ...actualDataToSave.map((item) => [item.id, item] as [string, EntityType<T>]),
            ]);
            return;
        }
        const resultSave = await this.save(actualDataToSave, typeSave);
        dataToSave.forEach((item) =>
            typeSave === 'modify'
                ? (item.sys_row_status = 'ACT')
                : typeSave === 'save_template'
                  ? (item.sys_row_status = 'TMP')
                  : typeSave === 'savedraft'
                    ? (item.sys_row_status = 'DRF')
                    : item.sys_row_status
        );

        for (const daoChild of this.daoChildren) {
            await daoChild.dao.saveLocalData(daoChild.params, actualDataToSave);
        }
        if (Array.isArray(data)) {
            return resultSave;
        }
        const id = typeof data.id === 'string' ? data.id : data.id?.value;
        if (!id) return;
        for (const [index, defer] of this.deferred.entries()) {
            await (
                'saveFn' in defer
                    ? defer.saveFn(defer.props)
                    : defer.entity.newDao().saveAll(
                          defer.data.map((item) => ({
                              ...item,
                              ...defer.applyFn(id),
                          }))
                      )
            )
                .then(defer.onSuccess)
                .catch((error) => {
                    defer.onError(error);
                    return Promise.reject(error);
                });
            this.deferred.splice(index, 1);
        }

        return resultSave;
    }

    public hookBeforeSave(hook: (data: Array<EntityType<T>>) => void): () => void {
        this.hooks.beforeSave.push(hook);
        const index = this.hooks.beforeSave.length - 1;
        return () => this.hooks.beforeSave.splice(index, 1);
    }

    public async deferSave<E extends Entity, EType extends typeof Entity>(
        entity: EType,
        data: Array<EntityType<E>> | EntityType<E>,
        applyFn: (id: string) => Partial<EntityType<E>>
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.deferred.push({
                entity,
                data: Array.isArray(data) ? data : [data],
                applyFn,
                onSuccess: resolve,
                onError: reject,
            });
        });
    }

    public async deferSaveFn<T>(saveFn: (props: T) => void | Promise<void>, props?: T): Promise<void> {
        return new Promise((resolve, reject) => {
            this.deferred.push({
                saveFn: () =>
                    new Promise((resolve, reject) => {
                        try {
                            const result = saveFn(props as any);
                            if (result instanceof Promise) result.then(resolve, reject);
                            else resolve(result);
                        } catch (err) {
                            reject(err);
                        }
                    }),
                props,
                onSuccess: resolve,
                onError: reject,
            });
        });
    }

    // public attachChild<K extends string>(
    //     field: keyof EntityType<T>,
    //     attachedDaoName: StringLiteral<K>
    // ): Dao<T, R> & { [typeof attachedDaoName]: any } {
    //     const ref = this.entityClass._struct[field as string]?.ref;
    //     if (!ref) throw new Error(`field ${field} does not have ref! [Table ${this.entityClass._tableInfo.tableName}]`);
    //     const { schemaName, tableName } = parseTableRef(ref);
    //     const refTable = resolveEntity(tableName as string, schemaName || undefined);
    //     if (!refTable) throw new Error(`Can not find table ${schemaName}.${tableName}`);
    //     const attachedThis = Object.assign({}, this, { [attachedDaoName]: refTable.newDao() });
    //     return attachedThis;
    // }
    bindToGrid(gridId: string) {
        this.gridId = gridId;
    }
    unbindFromGrid() {
        this.gridId = null;
    }

    protected getGridApi(): (() => GridApi) | undefined {
        if (!this.gridId) return undefined;
        const gridParams = GridStore.getParams(this.gridId);
        return gridParams?.ready ? () => gridParams.getGridApi() : undefined;
    }

    dependsOn<TK extends Entity, RK extends typeof Entity>(dao: Dao<TK, RK>, params: DepParams<RK, T>): Dao<T, R> {
        dao.daoChildren.push({ dao: this, params });
        this.isChildren = true;
        return this;
    }
    clearUnsavedData() {
        this.unsavedData.clear();
    }
    protected async saveLocalData(params: DepParams<any, any>, parentData: Array<EntityType<Entity>>) {
        let localData = [...this.unsavedData.values()];
        if ('idField' in params) {
            localData = localData.map((item) => ({ ...item, [params.idField]: parentData[0]?.id }));
        } else if ('preprocess' in params) {
            localData = localData.map((item) => params.preprocess(parentData[0], item));
        }
        await this.saveAll(localData, true);
        this.clearUnsavedData();
    }
}
